import {
  startPortalPayment,
  type PortalLeaseOut,
  type PortalPayOut,
} from '@myrentalmate/api-client';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { okData } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { formatMoney } from '../../lib/format';

let stripeForAccount: { account: string; promise: Promise<Stripe | null> } | null = null;

/** Stripe's script loads only when someone opens this, and only once per account. */
function stripeFor(publishableKey: string, account: string) {
  if (stripeForAccount?.account !== account) {
    stripeForAccount = {
      account,
      promise: loadStripe(publishableKey, { stripeAccount: account }),
    };
  }
  return stripeForAccount.promise;
}

type Method = 'card' | 'ach';

/** Stripe's smallest charge. */
const MINIMUM = 0.5;

function ConfirmForm({
  amount,
  method,
  onDone,
}: {
  amount: string;
  method: Method;
  onDone: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!stripe || !elements) return;
    setPaying(true);
    setError(null);
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/portal/payments` },
      redirect: 'if_required',
    });
    if (result.error) {
      setError(result.error.message ?? 'That payment didn’t go through.');
      setPaying(false);
      return;
    }
    onDone();
  }

  return (
    <form onSubmit={(e) => void submit(e)}>
      <DialogContent>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <PaymentElement />
          {method === 'ach' && (
            <Typography variant="body2" color="text.secondary">
              Bank payments take several business days to clear.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button type="submit" variant="contained" disabled={!stripe || paying}>
          {paying ? 'Paying…' : `Pay ${formatMoney(amount)}`}
        </Button>
      </DialogActions>
    </form>
  );
}

/** Choose how much and how, then Stripe's own form. Card details never touch this app. */
export function PayDialog({
  lease,
  pay,
  onClose,
}: {
  lease: PortalLeaseOut;
  pay: PortalPayOut;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const payable = Math.max(Number(lease.balance) - Number(lease.processing), 0).toFixed(2);
  const [amount, setAmount] = useState(payable);
  const [method, setMethod] = useState<Method>('card');
  const [secret, setSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function start(event: FormEvent) {
    event.preventDefault();
    const cleaned = amount.replace(/[$,\s]/g, '');
    if (!/^\d+(\.\d{1,2})?$/.test(cleaned) || Number(cleaned) < MINIMUM) {
      setError(`Enter an amount of at least ${formatMoney(MINIMUM)}, like 1250.00.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const started = okData(
        await startPortalPayment({ lease_id: lease.id, amount: cleaned, method }),
      );
      if (started) {
        setAmount(cleaned);
        setSecret(started.client_secret);
      }
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  function finished() {
    setDone(true);
    // The books change when Stripe confirms, a moment later.
    window.setTimeout(() => void queryClient.invalidateQueries(), 3000);
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Pay {lease.label}</DialogTitle>
      {done ? (
        <>
          <DialogContent>
            <Alert severity="success">
              {method === 'ach'
                ? 'Your bank payment is on its way. It takes several business days to clear.'
                : 'Payment sent. Your balance and receipt update in a moment.'}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Done</Button>
          </DialogActions>
        </>
      ) : secret && pay.publishable_key && pay.stripe_account ? (
        <Elements
          stripe={stripeFor(pay.publishable_key, pay.stripe_account)}
          options={{ clientSecret: secret }}
        >
          <ConfirmForm amount={amount} method={method} onDone={finished} />
        </Elements>
      ) : (
        <form onSubmit={(e) => void start(e)}>
          <DialogContent>
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField
                required
                label="Amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                helperText={`You owe ${formatMoney(payable)}.`}
                slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              />
              {pay.ach && (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  color="primary"
                  aria-label="Pay by"
                  value={method}
                  onChange={(_, value: Method | null) => value && setMethod(value)}
                >
                  <ToggleButton value="card">Card</ToggleButton>
                  <ToggleButton value="ach">Bank account</ToggleButton>
                </ToggleButtonGroup>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={busy}>
              Continue
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
}
